import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Card, Group, ThemeIcon, Box, Stack,
  Button, Badge, Divider, UnstyledButton, Drawer, TextInput,
  Textarea, Checkbox, NumberInput, ActionIcon, Skeleton, Tooltip,
  ColorPicker,
} from '@mantine/core';
import {
  IconBook2, IconUser, IconArrowLeft, IconPlus,
  IconPencil, IconTrash, IconRefresh,
  IconMapPin,
} from '@tabler/icons-react';
import { Table, ConfirmDialog } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useLeaderTypeService, LeaderType } from '@actschurch/ccms-sdk';

// ── Sidebar ───────────────────────────────────────────────────────────────────

const ALL_CATEGORIES = [
  { label: 'Acts Care Courses',    icon: IconBook2, link: '/LookUp/ActsCareCourses',    active: false },
  { label: 'Acts Care Counseling', icon: IconUser,  link: '/LookUp/ActsCareCounseling', active: true  },
  { label: 'Locations', icon: IconMapPin,  link: '/LookUp/Location', active: false  },
];

const EMPTY: LeaderType = {
  typeID: 0,
  name: '',
  description: '',
  color: '',
  icon: '',
  isActive: false,
  createdDateTime: 0,
  modifiedDateTime: 0,
};

// ── Shimmer ───────────────────────────────────────────────────────────────────

function ShimmerRow() {
  return (
    <Table.Tr>
      {Array.from({ length: 7 }).map((_, i) => (
        <Table.Td key={i}>
          <Skeleton height={14} radius="sm" />
        </Table.Td>
      ))}
    </Table.Tr>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function LeaderTypePage() {
  const navigate = useNavigate();
  const service = useLeaderTypeService();

  const [types, setTypes]             = useState<LeaderType[]>([]);
  const [loading, setLoading]         = useState(false);
  const [addOpen, setAddOpen]         = useState(false);
  const [editOpen, setEditOpen]       = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm]               = useState<LeaderType>(EMPTY);
  const [selectedID, setSelectedID]   = useState(0);

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      setTypes(await service.getAll());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => setForm(EMPTY);

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const handleAdd = async () => {
    setLoading(true);
    try {
      await service.create(form);
      await fetchAll();
      resetForm();
      setAddOpen(false);
      toast.success('Created', 'Leader type created successfully.');
    } catch (e) {
      console.error(e);
      setLoading(false);
      toast.error('Error', 'Failed to create leader type.');
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await service.update(selectedID, form);
      await fetchAll();
      resetForm();
      setEditOpen(false);
      toast.success('Updated', 'Leader type updated successfully.');
    } catch (e) {
      console.error(e);
      setLoading(false);
      toast.error('Error', 'Failed to update leader type.');
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await service.delete(selectedID);
      await fetchAll();
      setConfirmOpen(false);
      toast.warning('Deleted', 'Leader type deleted successfully.');
    } catch (e) {
      console.error(e);
      setLoading(false);
      toast.error('Error', 'Failed to delete leader type.');
    }
  };

  const openEdit = (t: LeaderType) => {
    setSelectedID(t.typeID);
    setForm({ ...t });
    setEditOpen(true);
  };

  // ── Shared form ───────────────────────────────────────────────────────────

  const TypeForm = (
    <Stack>
      <TextInput
        label="Name"
        value={form.name ?? ''}
        onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
      />
      <Text>Choose Colour</Text>
      <ColorPicker
        value={form.color || '#4CAF50'}
        onChange={(value) => setForm({ ...form, color: value })}
        format="hex"
        swatches={[
          '#e03131', '#c2255c', '#9c36b5', '#3b5bdb', '#1971c2',
          '#0c8599', '#099268', '#2f9e44', '#e8590c', '#f08c00',
        ]}
        swatchesPerRow={10}
        fullWidth
      />
      <TextInput
        label="Icon"
        value={form.icon ?? ''}
        onChange={(e) => setForm({ ...form, icon: e.currentTarget.value })}
      />
      <Textarea
        label="Description"
        value={form.description ?? ''}
        onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
      />
      <Checkbox
        label="Is Active"
        checked={form.isActive}
        onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
      />
    </Stack>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container size="xl" py="xl">
      {/* Add drawer */}
      <Drawer
        opened={addOpen}
        onClose={() => { setAddOpen(false); resetForm(); }}
        title="Add Leader Type"
        position="right" size="md" padding="md"
      >
        <NumberInput
          label="Type ID"
          required
          value={form.typeID}
          onChange={(v) => setForm({ ...form, typeID: Number(v) })}
        />
        {TypeForm}
        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={() => { setAddOpen(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleAdd}>Save</Button>
        </Group>
      </Drawer>

      {/* Edit drawer */}
      <Drawer
        opened={editOpen}
        onClose={() => { setEditOpen(false); resetForm(); }}
        title="Update Leader Type"
        position="right" size="md" padding="md"
      >
        {TypeForm}
        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={() => { setEditOpen(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleUpdate}>Update</Button>
        </Group>
      </Drawer>

      {/* Delete confirm — outside the map, single instance */}
      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        type="danger"
        title="Delete this leader type?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <Group align="flex-start" gap="xl" wrap="nowrap">

        {/* Sidebar */}
        <Box w={220} style={{ flexShrink: 0 }}>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            mb="lg" px={0}
            onClick={() => navigate('/LookUp/ActsCareCounseling')}
          >
            All Lookups
          </Button>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm">Categories</Text>
          <Stack gap={4}>
            {ALL_CATEGORIES.map((cat) => (
              <UnstyledButton
                key={cat.label}
                onClick={() => navigate(cat.link)}
                style={(theme) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: theme.radius.sm,
                  backgroundColor: cat.active ? theme.colors.teal[9] + '33' : 'transparent',
                  color: cat.active ? theme.colors.teal[4] : theme.colors.gray[4],
                  fontWeight: cat.active ? 600 : 400,
                })}
              >
                <ThemeIcon
                  variant={cat.active ? 'light' : 'subtle'}
                  color={cat.active ? 'teal' : 'gray'}
                  size="sm" radius="sm"
                >
                  <cat.icon size={14} />
                </ThemeIcon>
                <Text size="sm">{cat.label}</Text>
              </UnstyledButton>
            ))}
          </Stack>
        </Box>

        <Divider orientation="vertical" />

        {/* Main panel */}
        <Box style={{ flex: 1 }}>
          <Group justify="space-between" mb="lg">
            <Group gap="md">
              <ThemeIcon variant="light" color="teal" size="lg" radius="md">
                <IconUser size={18} />
              </ThemeIcon>
              <div>
                <Title order={3}>Leader Type</Title>
                <Text size="sm" c="dimmed">Pastor, Counselor, Volunteer leader roles</Text>
              </div>
            </Group>
            <Group>
              <Tooltip label="Refresh">
                <ActionIcon variant="subtle" onClick={fetchAll} loading={loading}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
              <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)}>
                Add Item
              </Button>
            </Group>
          </Group>

          <Card withBorder radius="md" padding="lg">
            <Table tableVariant="default">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Icon</Table.Th>
                  <Table.Th>Color</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => <ShimmerRow key={i} />)
                  : types.map((t) => (
                      <Table.Tr key={t.typeID}>
                        <Table.Td>{t.typeID}</Table.Td>
                        <Table.Td fw={600}>{t.name}</Table.Td>
                        <Table.Td>{t.icon}</Table.Td>
                        <Table.Td>
                          {t.color ? (
                            <Group gap={6}>
                              <Box
                                w={16} h={16}
                                style={{
                                  borderRadius: 4,
                                  backgroundColor: t.color,
                                  border: '1px solid rgba(255,255,255,0.15)',
                                  flexShrink: 0,
                                }}
                              />
                              <Text size="sm">{t.color}</Text>
                            </Group>
                          ) : '—'}
                        </Table.Td>
                        <Table.Td>{t.description}</Table.Td>
                        <Table.Td>
                          <Badge
                            color={t.isActive ? 'green' : 'red'}
                            variant="light"
                            size="sm"
                          >
                            {t.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <ActionIcon variant="subtle" onClick={() => openEdit(t)}>
                              <IconPencil size={16} color="orange" />
                            </ActionIcon>
                            <ActionIcon variant="subtle" onClick={() => {
                              setSelectedID(t.typeID);
                              setConfirmOpen(true);
                            }}>
                              <IconTrash size={16} color="red" />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
              </Table.Tbody>
            </Table>
          </Card>
        </Box>
      </Group>
    </Container>
  );
}